import { Grid, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import * as api from '../../api/app/monitoredStateApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { AssetFullPathHeader } from '../../components/core/AssetFullPathHeader/AssetFullPathHeader';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { AssetDetailTabValues, MonitoredPanelValues } from '../../types/app/asset.types';
import { MonitoredState, MonitoredStateInputs } from '../../types/app/monitoredState.types';
import { dictionary } from '../../utils/core/dictionary';
import { paths } from '../../utils/core/routes';
import { MonitoredStateForm } from './index';
import { useStyles } from './MonitoredStateForm.styles';

export function MonitoredStateDetail() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id, parentId } = useParams();
  const [defaultValues, setDefaultValues] = useState<MonitoredStateInputs | null>(null);

  const { mutate: mutateUpdateMonitoredState } = useMutation(api.updateMonitoredState);
  const { data, isLoading, isError } = useQuery<MonitoredState>(['monitoredState', id], () =>
    api.getMonitoredState(Number(id)),
  );

  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      parentAssetId: data.parentAssetId,
      opcClientId: data.opcClientId,
      influxValueName: data.influxValueName,
      nodeId: data.nodeId,
      name: data.name,
      main: data.main,
      dataType: data.dataType,
    });
  }, [data]);

  const handleOnSubmit: SubmitHandler<MonitoredStateInputs> = (data: MonitoredStateInputs) => {
    if (!defaultValues) return;

    mutateUpdateMonitoredState(
      { ...data },
      {
        onSuccess: () => {
          enqueueSnackbar(dictionary.MONITORED_STATE_EDITED, { variant: 'success' });
          navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
            state: {
              tabValue: AssetDetailTabValues.MONITORED_VALUES,
              panelValue: MonitoredPanelValues.MONITORED_STATE,
            },
          });
        },
        onError: (result: any) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  if (isError) return <ApiError />;
  if (isLoading || !defaultValues) return <Loader />;

  return (
    <>
      <AssetFullPathHeader
        finalTitle={dictionary.MONITORED_STATE_EDIT}
        parentId={Number(parentId)}
      />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Grid item xs={12} md={8} lg={4}>
              <Paper className={classes.paper}>
                <MonitoredStateForm
                  onSubmit={handleOnSubmit}
                  onBack={() =>
                    navigate(`${paths.ASSETS_DETAIL}/${parentId}`, {
                      state: {
                        tabValue: AssetDetailTabValues.MONITORED_VALUES,
                        panelValue: MonitoredPanelValues.MONITORED_STATE,
                      },
                    })
                  }
                  defaultValues={defaultValues}
                />
              </Paper>
            </Grid>
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
